import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import PdfGenerator from '../ReportPages/PdfGenerator/PdfGenerator';

// Store
import { Dispatch } from '../../store/store';
import { MetaField } from '../../store/models/cycles';
import { FieldUpdate } from '../FieldInput/interfaces/field-update';
import { FieldInput } from '../FieldInput/FieldInput';

const ReportMetaFields = ({
  getMetaFields,
  addMetaField,
  removeMetaField,
  saveAsDefault,
  loadDefaultTags,
  updateMetaFields,
}: {
  cycleReportId?: string;
  getMetaFields: (data: { id: string }) => Promise<MetaField[]>;
  addMetaField: (data: { id: string; metaField: MetaField }) => Promise<void>;
  removeMetaField: (data: { id: string; key: string }) => Promise<void>;
  saveAsDefault: (data: { id: string }) => Promise<void>;
  loadDefaultTags: (data: { id: string }) => Promise<MetaField[]>;
  updateMetaFields: (data: { id: string; updatedFields: Record<string, string> }) => Promise<MetaField[]>;
}) => {
  const { id: cycleId } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [newField, setNewField] = useState<MetaField>({ key: '', value: '' });
  const [metaFields, setMetaFields] = useState<MetaField[]>([]);
  const [fieldUpdates, setFieldUpdates] = useState<Record<string, FieldUpdate>>({});

  // Fetch existing meta fields when component mounts
  const fetchMetaFields = useCallback(async () => {
    try {
      const response = await getMetaFields({ id: cycleId });
      setMetaFields(response);
    } catch (error) {
      console.error('Error fetching meta fields:', error);
    }
  }, [cycleId]);

  const saveAsDefaultHandler = () => {
    saveAsDefault({ id: cycleId });
  };

  const loadDefaultTagsHandler = async () => {
    setIsLoading(true);
    const metaFields = await loadDefaultTags({ id: cycleId });
    setMetaFields(metaFields);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchMetaFields();
  }, [fetchMetaFields]);

  const handleAddField = async () => {
    if (!newField.key.trim() || !newField.value.trim()) return;

    try {
      setIsLoading(true);

      await addMetaField({
        id: cycleId,
        metaField: newField,
      });

      setMetaFields((prevMetaFields) => [...prevMetaFields, newField]);
      setNewField({ key: '', value: '' });
    } catch (error) {
      console.error('Error adding new field:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveField = async (key: string) => {
    try {
      await removeMetaField({ id: cycleId, key });

      setMetaFields((prevMetaFields) => prevMetaFields.filter((field) => field.key !== key));
    } catch (error) {
      console.error('Error removing meta tag:', error);
    }
  };

  const handleFieldUpdate = (originalKey: string, updatedField: MetaField) => {
    setFieldUpdates((prev) => ({
      ...prev,
      [originalKey]: {
        originalKey,
        field: updatedField
      }
    }));
  };

  const handleUpdateAll = async () => {
    try {
      setIsLoading(true);

      // Get the final state of all fields
      const finalFields = metaFields.map(field => {
        const update = fieldUpdates[field.key];
        return update ? update.field : field;
      });

      // Transform into a single object with all key-value pairs
      const formattedFields = finalFields.reduce((acc, field) => {
        acc[field.key] = field.value;
        return acc;
      }, {} as Record<string, string>);

      // Send all field updates at once
      const response = await updateMetaFields({
        id: cycleId,
        updatedFields: formattedFields
      });

      setMetaFields(response);
      // Reset field updates after successful update
      setFieldUpdates({});
    } catch (error) {
      console.error('Error updating fields:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="c-report-meta-fields mt-10 rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
        <h3 className="font-medium text-black dark:text-white">Meta Fields</h3>
      </div>

      <div className="p-7">
        <div className="flex flex-col gap-5.5">
          {metaFields.length === 0 ? (
            <p className="text-gray-500 text-sm">No meta fields available. Add new fields below.</p>
          ) : (
            <div>
              {metaFields.map((field) => (
                <FieldInput
                  key={field.key}
                  field={fieldUpdates[field.key]?.field || field}
                  originalField={field}
                  onRemove={handleRemoveField}
                  onUpdate={(updatedField) => handleFieldUpdate(field.key, updatedField)}
                />
              ))}
              <button
                onClick={handleUpdateAll}
                className="rounded-md bg-primary px-6 py-2 text-center font-medium text-white hover:bg-opacity-90 disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? 'Updating...' : 'Update'}
              </button>
            </div>
          )}
        </div>

        <div className="mt-7.5">
          <h3 className="mb-4 text-lg font-medium text-black dark:text-white">Add New Field</h3>
          <FieldInput field={newField} onChange={(updatedField) => setNewField(updatedField)} />
        </div>

        <div className="mt-5.5 flex flex-col md:flex-row gap-4">
          <button
            onClick={handleAddField}
            className="inline-flex items-center justify-center rounded-md bg-secondary px-4 py-1.5 text-center font-medium text-white hover:bg-opacity-90 disabled:opacity-50"
            disabled={isLoading || !newField.key.trim() || !newField.value.trim()}
          >
            {isLoading ? 'Adding...' : 'Add'}
          </button>
          <PdfGenerator />
          <button
            onClick={saveAsDefaultHandler}
            className="inline-flex items-center justify-center rounded-md bg-meta-3 px-4 py-1.5 text-center font-medium text-white hover:bg-opacity-90 disabled:opacity-50"
            disabled={Boolean(newField.key.trim() || newField.value.trim())}
          >
            Save as Default
          </button>
          <button
            onClick={loadDefaultTagsHandler}
            className="inline-flex items-center justify-center rounded-md bg-secondary px-4 py-1.5 text-center font-medium text-white hover:bg-opacity-90 disabled:opacity-50"
            disabled={Boolean(newField.key.trim() || newField.value.trim())}
          >
            Load Default Tags
          </button>
        </div>
      </div>
    </div>
  );
};

const mapDispatch = (dispatch: Dispatch) => ({
  getMetaFields: dispatch.cycles.getMetaFields,
  addMetaField: dispatch.cycles.addMetaField,
  removeMetaField: dispatch.cycles.removeMetaField,
  saveAsDefault: dispatch.cycles.saveAsDefault,
  loadDefaultTags: dispatch.cycles.loadDefaultTags,
  updateMetaFields: dispatch.cycles.updateMetaFields,
});

export default connect(null, mapDispatch)(ReportMetaFields);
